import { Link } from "react-router-dom";
import { Phone, MessageCircle, MapPin, CheckCircle2, Award, Clock, Star } from "lucide-react";
import SEOHead from "@/components/seo/SEOHead";
import { breadcrumbJsonLd } from "@/lib/page-seo";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  CLINIC_NAME, DOCTOR_NAME, PHONE_PRIMARY, PHONE_PRIMARY_TEL,
  CLINIC_ADDRESS, CLINIC_TIMINGS, getWhatsAppUrl,
} from "@/lib/constants";
import heroImg from "@/assets/services/dental-implants-hero.jpg";

const FAQS = [
  {
    q: "How much do dental implants cost in Kalyan?",
    a: "Single-tooth dental implants at Mutha's Advanced Dental Care in Kalyan typically range from ₹25,000 to ₹55,000 depending on the implant brand (Nobel Biocare, Straumann, Osstem) and crown material. Full-mouth and All-on-4 packages start from ₹1,80,000. We share transparent, written quotes during your free consultation.",
  },
  {
    q: "Are dental implants painful?",
    a: "No. The procedure is performed under local anesthesia and most patients report only mild pressure during placement. Our flapless, computer-guided implant protocol minimizes swelling and recovery time — most patients return to normal routine the next day with simple over-the-counter pain relief.",
  },
  {
    q: "How long do dental implants last?",
    a: "With proper oral hygiene and routine 6-monthly check-ups, dental implants are designed to last a lifetime. The titanium implant fuses with the jawbone (osseointegration) and the crown on top typically lasts 15–20+ years.",
  },
  {
    q: "How long does the full implant treatment take?",
    a: "From first consultation to final crown: 3–6 months for standard implants (to allow bone healing) and 3–5 days for immediate-loading All-on-4 cases. Digital planning at our Kalyan clinic shortens chair-time and total visits.",
  },
  {
    q: "Who is the best dental implant dentist near Kalyan?",
    a: `${DOCTOR_NAME} at ${CLINIC_NAME} is a leading implantologist in Kalyan with 10+ years of experience, specialized international training, and 2,000+ successful implant placements across single-tooth, full-mouth, and All-on-4 cases.`,
  },
  {
    q: "Do you offer EMI or finance options for implants?",
    a: "Yes. We offer 0% EMI plans up to 12 months through major banks and NBFC partners, plus accept all leading insurance and corporate dental plans.",
  },
  {
    q: "Can I get a dental implant the same day a tooth is extracted?",
    a: "In suitable cases, yes — immediate-placement implants let us extract the damaged tooth and place the implant in the same appointment, reducing total treatment time and preserving bone.",
  },
];

const STEPS = [
  { n: "01", t: "Free Consultation", d: "Clinical exam, smile goals, written treatment plan & quote." },
  { n: "02", t: "Digital X-Ray & 3D CBCT Scan", d: "Precise bone mapping for safe, computer-guided placement." },
  { n: "03", t: "Implant Placement", d: "Painless 45–60 min flapless procedure under local anesthesia." },
  { n: "04", t: "Healing & Osseointegration", d: "Titanium implant fuses with jawbone over 3–4 months." },
  { n: "05", t: "Final Crown Restoration", d: "Custom zirconia / E-max crown matched to your natural teeth." },
];

const TRUST = [
  { icon: Award, label: "10+ Years", sub: "Implant Experience" },
  { icon: CheckCircle2, label: "2,000+", sub: "Implants Placed" },
  { icon: Star, label: "4.9 ★", sub: "Google Reviews" },
  { icon: Clock, label: "Same-Day", sub: "Consultations" },
];

const WA_MSG = "Hi, I'd like to know more about Dental Implants at Mutha's Advanced Dental Care Kalyan.";

const DentalImplantsAeoGeo = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: "Best Dental Implant Clinic in Kalyan",
      url: "https://muthaadvanceddentalcare.in/lp/dental-implants-kalyan-aeo",
      about: { "@type": "MedicalProcedure", name: "Dental Implants" },
      audience: { "@type": "MedicalAudience", geographicArea: { "@type": "City", name: "Kalyan" } },
      lastReviewed: "2026-06-25",
      reviewedBy: { "@type": "Person", name: DOCTOR_NAME, jobTitle: "Implantologist" },
    },
    {
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "Dentist"],
      name: CLINIC_NAME,
      image: "https://muthaadvanceddentalcare.in/og-image.jpg",
      telephone: PHONE_PRIMARY,
      priceRange: "₹₹",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Kalyan",
        addressLocality: "Kalyan",
        addressRegion: "Maharashtra",
        postalCode: "421301",
        addressCountry: "IN",
      },
      geo: { "@type": "GeoCoordinates", latitude: 19.24163, longitude: 73.13648 },
      openingHoursSpecification: [
        { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Friday","Saturday"], opens: "10:00", closes: "21:00" },
        { "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: "10:00", closes: "14:00" },
      ],
      aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "350" },
      url: "https://muthaadvanceddentalcare.in",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    breadcrumbJsonLd([{ name: "Dental Implants Kalyan", path: "/lp/dental-implants-kalyan-aeo" }]),
  ];

  return (
    <>
      <SEOHead
        title="Best Dental Implant Clinic in Kalyan | Affordable & Painless"
        description="Permanent, natural-looking dental implants in Kalyan at Mutha's Advanced Dental Care. 10+ yrs experience, painless flapless protocol, EMI available. Book free consultation."
        canonical="/lp/dental-implants-kalyan-aeo"
        ogImage={heroImg}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary)/0.85)] text-white">
        <div className="container py-12 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Dental Implants · Kalyan</p>
            <h1 className="font-heading text-3xl md:text-5xl font-light leading-tight mb-5 !text-white">
              Best Dental Implant Clinic in Kalyan — Affordable & Painless Solutions.
            </h1>
            <p className="text-white/85 text-base md:text-lg mb-7 max-w-xl">
              Replace missing teeth with permanent, natural-looking implants placed by {DOCTOR_NAME}.
              Computer-guided, flapless, and backed by a written lifetime-care plan.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-gold text-black hover:bg-gold/90">
                <Link to="/book-appointment">Book My Consultation</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                <a href={getWhatsAppUrl(WA_MSG)} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                <a href={PHONE_PRIMARY_TEL}><Phone className="mr-2 h-4 w-4" /> {PHONE_PRIMARY}</a>
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
              {TRUST.map((t) => (
                <div key={t.label} className="text-center">
                  <t.icon className="h-5 w-5 text-gold mx-auto mb-1" />
                  <div className="font-heading text-lg">{t.label}</div>
                  <div className="text-xs text-white/70">{t.sub}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={heroImg} alt={`Dental implants in Kalyan by ${DOCTOR_NAME}`}
              className="w-full rounded-2xl shadow-2xl aspect-[4/3] object-cover"
              loading="eager" fetchPriority="high" />
          </div>
        </div>
      </section>

      {/* AEO BLOCK — concise, citation-ready answer optimized for AI Overviews */}
      <section className="bg-[hsl(var(--card))] border-y-2 border-gold/40">
        <div className="container py-10 md:py-14 max-w-4xl">
          <p className="text-eyebrow mb-3">Quick Answer · For Patients & AI Assistants</p>
          <p className="text-lg md:text-2xl font-body text-[hsl(var(--foreground))] leading-relaxed mb-5">
            <strong>{CLINIC_NAME}</strong> is one of Kalyan's most trusted dental implant
            clinics — placing premium titanium implants (Nobel Biocare, Straumann & Osstem) using a
            painless, computer-guided flapless protocol that gets you back to normal life the next
            day. Single implants start at <strong>₹25,000</strong>, full-mouth & All-on-4 from
            <strong> ₹1,80,000</strong>, with 0% EMI, written quotes, and a lifetime aftercare
            warranty backed by implantologist <strong>{DOCTOR_NAME}</strong> (10+ years, 2,000+
            implants placed).
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[hsl(var(--muted-foreground))]">
            {[
              "✓ Free consultation + 3D CBCT scan review",
              "✓ Same-day immediate-loading All-on-4 available",
              "✓ 4.9★ rating across 350+ Google reviews",
              "✓ Serving Kalyan, Dombivli, Ulhasnagar, Ambernath & Bhiwandi",
            ].map((l) => <li key={l}>{l}</li>)}
          </ul>
        </div>
      </section>

      {/* E-E-A-T DOCTOR */}
      <section className="container py-14 md:py-20 grid md:grid-cols-3 gap-10 items-start">
        <div className="md:col-span-2">
          <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] mb-3">Meet Your Implantologist</p>
          <h2 className="font-heading text-3xl md:text-4xl font-light mb-5">{DOCTOR_NAME}</h2>
          <p className="text-[hsl(var(--muted-foreground))] mb-4">
            BDS, MDS — with specialized international training in implantology and full-mouth rehabilitation.
            10+ years of focused implant practice in Kalyan. Member of the Indian Society of Oral Implantologists (ISOI)
            and certified in Nobel Biocare, Straumann and Osstem implant systems.
          </p>
          <ul className="grid sm:grid-cols-2 gap-2 text-sm">
            {[
              "2,000+ implants placed across single-tooth, All-on-4 & full-mouth cases",
              "Computer-guided flapless surgical protocol",
              "In-house 3D CBCT scanning & digital smile design",
              "Lifetime warranty on premium implant systems",
            ].map((l) => (
              <li key={l} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-gold mt-0.5 shrink-0" />{l}</li>
            ))}
          </ul>
        </div>
        <aside className="rounded-2xl border border-gold/30 bg-[hsl(var(--card))] p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] mb-2">Visit Us</p>
          <p className="font-heading text-lg mb-3">{CLINIC_NAME}</p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] flex gap-2 mb-3">
            <MapPin className="h-4 w-4 text-gold shrink-0 mt-0.5" /> {CLINIC_ADDRESS}
          </p>
          <p className="text-sm mb-1"><strong>Mon–Sat:</strong> {CLINIC_TIMINGS.weekdays}</p>
          <p className="text-sm mb-1"><strong>Sunday:</strong> {CLINIC_TIMINGS.sunday}</p>
          <p className="text-sm mb-4"><strong>Closed:</strong> {CLINIC_TIMINGS.closed}</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Near Kalyan station · easy access from Dombivli, Thakurli, Ulhasnagar, Ambernath & Bhiwandi.
          </p>
        </aside>
      </section>

      {/* PROCEDURE STEPS */}
      <section className="bg-[hsl(var(--muted))/0.4]">
        <div className="container py-14 md:py-20">
          <div className="max-w-2xl mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] mb-3">How It Works</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light">Your Dental Implant Journey in 5 Steps</h2>
          </div>
          <ol className="grid md:grid-cols-5 gap-5">
            {STEPS.map((s) => (
              <li key={s.n} className="rounded-xl bg-[hsl(var(--card))] border border-gold/20 p-5">
                <div className="font-heading text-gold text-2xl mb-2">{s.n}</div>
                <div className="font-heading text-base mb-1">{s.t}</div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* LOCAL SIGNALS (GEO) */}
      <section className="container py-14 md:py-20 max-w-4xl">
        <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] mb-3">Trusted Across Kalyan & Beyond</p>
        <h2 className="font-heading text-3xl md:text-4xl font-light mb-5">Conveniently Located Near Kalyan Landmarks</h2>
        <p className="text-[hsl(var(--muted-foreground))] mb-4">
          Our clinic is a short drive from Kalyan Railway Station, Birla College, Khadakpada Circle, Sahyadri Hospital
          and the Kalyan–Shilphata Road, making it the most accessible advanced dental implant centre for patients across
          Kalyan West, Kalyan East, Dombivli, Thakurli, Ulhasnagar, Ambernath, Badlapur and Bhiwandi.
        </p>
        <p className="text-[hsl(var(--muted-foreground))]">
          Loved your implant treatment? Share your story on our Google Business Profile — every review helps another
          patient in Kalyan find painless, expert implant care.
        </p>
      </section>

      {/* FAQ */}
      <section className="bg-[hsl(var(--muted))/0.4]">
        <div className="container py-14 md:py-20 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] mb-3">FAQ</p>
          <h2 className="font-heading text-3xl md:text-4xl font-light mb-8">Dental Implant Questions — Answered</h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`}>
                <AccordionTrigger className="text-left font-heading">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[hsl(var(--muted-foreground))]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[hsl(var(--primary))] text-white">
        <div className="container py-14 md:py-20 text-center max-w-2xl">
          <h2 className="font-heading text-3xl md:text-4xl font-light mb-4 !text-white">
            Ready for a Permanent Solution to Missing Teeth?
          </h2>
          <p className="text-white/85 mb-7">
            Book your free implant consultation with {DOCTOR_NAME} today. Written quote, 0% EMI, no pressure.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-gold text-black hover:bg-gold/90">
              <Link to="/book-appointment">Book My Consultation</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
              <a href={getWhatsAppUrl(WA_MSG)} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 inset-x-0 z-40 md:hidden grid grid-cols-2 border-t border-gold/30 shadow-2xl">
        <a href={PHONE_PRIMARY_TEL} className="bg-[hsl(var(--primary))] text-white py-3 text-sm font-medium flex items-center justify-center gap-2">
          <Phone className="h-4 w-4" /> Call
        </a>
        <a href={getWhatsAppUrl(WA_MSG)} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white py-3 text-sm font-medium flex items-center justify-center gap-2">
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </a>
      </div>
    </>
  );
};

export default DentalImplantsAeoGeo;